import React from "react";

const PatientsModal = ({ isOpen, onClose, patients }) => {
  if (!isOpen) return null;

  const totalFee = patients.reduce((sum, patient) => sum + patient.fee, 0);

  const formatDate = (dateString) => {
    if (!dateString) return "";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const formatTime = (dateString) => {
    if (!dateString) return "";
    return new Date(dateString).toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  };

  const latestCheckupDate =
    patients.length > 0
      ? formatDate(
          new Date(Math.max(...patients.map((p) => new Date(p.created_at)))),
        )
      : "N/A";

  return (
    <div
      className="fixed inset-0 bg-black/50 flex justify-center items-center z-50 p-2 sm:p-4 md:p-6"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full
          max-w-full sm:max-w-[90%] md:max-w-[75%] lg:max-w-[65%]
          max-h-[95vh] sm:max-h-[94vh] flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* ── Header ── */}
        <div className="bg-[#1F2937] px-4 py-3 sm:px-5 sm:py-4 flex items-center justify-between flex-shrink-0">
          <div className="min-w-0 mr-3">
            <p className="text-[10px] font-medium uppercase tracking-widest text-gray-400">
              Patient Records
            </p>
            <p className="text-white font-semibold text-sm sm:text-base leading-tight mt-0.5 truncate">
              Latest: {latestCheckupDate}
            </p>
          </div>
          <button
            onClick={onClose}
            className="flex-shrink-0 flex items-center justify-center w-8 h-8 rounded-full
              bg-white/10 text-gray-300 hover:bg-white/20 hover:text-white
              transition-all duration-200 text-lg font-bold leading-none"
          >
            &times;
          </button>
        </div>

        {/* ── Info Strip ── */}
        <div className="bg-gray-50 border-b border-gray-100 px-4 py-2.5 sm:px-5 sm:py-3 flex items-center gap-2 sm:gap-3 flex-shrink-0">
          <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-full bg-gray-800 flex items-center justify-center text-white font-bold text-sm flex-shrink-0">
            P
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-xs sm:text-sm font-semibold text-gray-800 truncate">
              All Patients
            </p>
            <p className="text-[11px] text-gray-400">
              {patients.length} record(s) found
            </p>
          </div>
          <span className="flex-shrink-0 text-[10px] sm:text-[11px] font-semibold px-2 py-0.5 sm:px-2.5 sm:py-1 rounded-full bg-blue-50 text-blue-600 border border-blue-100 whitespace-nowrap">
            Patients
          </span>
        </div>

        {/* ── Scrollable Table ── */}
        <div className="overflow-y-auto overflow-x-auto flex-1 px-3 py-3 sm:px-5 sm:py-4">
          {patients.length > 0 ? (
            <table className="w-full min-w-[520px] text-xs sm:text-sm border-collapse">
              <thead>
                <tr className="bg-gray-50">
                  <th className="text-[9px] sm:text-[10px] font-bold uppercase tracking-widest text-gray-400 px-3 py-2.5 text-left whitespace-nowrap">
                    #
                  </th>
                  <th className="text-[9px] sm:text-[10px] font-bold uppercase tracking-widest text-gray-400 px-3 py-2.5 text-left whitespace-nowrap">
                    Date
                  </th>
                  <th className="text-[9px] sm:text-[10px] font-bold uppercase tracking-widest text-gray-400 px-3 py-2.5 text-left whitespace-nowrap">
                    Time
                  </th>
                  <th className="text-[9px] sm:text-[10px] font-bold uppercase tracking-widest text-gray-400 px-3 py-2.5 text-left whitespace-nowrap">
                    Patient
                  </th>
                  <th className="text-[9px] sm:text-[10px] font-bold uppercase tracking-widest text-gray-400 px-3 py-2.5 text-left whitespace-nowrap">
                    Doctor
                  </th>
                  <th className="text-[9px] sm:text-[10px] font-bold uppercase tracking-widest text-gray-400 px-3 py-2.5 text-right whitespace-nowrap">
                    Checkup Fee
                  </th>
                </tr>
              </thead>
              <tbody>
                {patients.map((patient, index) => (
                  <tr
                    key={index}
                    className="border-b border-gray-100 hover:bg-gray-50 transition-colors"
                  >
                    <td className="px-3 py-2.5 text-gray-400 font-medium whitespace-nowrap">
                      {index + 1}
                    </td>
                    <td className="px-3 py-2.5 text-gray-600 whitespace-nowrap">
                      {formatDate(patient.created_at)}
                    </td>
                    <td className="px-3 py-2.5 text-gray-600 whitespace-nowrap">
                      {formatTime(patient.created_at)}
                    </td>
                    <td className="px-3 py-2.5 text-gray-800 font-medium">
                      {patient.full_name}
                    </td>
                    <td className="px-3 py-2.5 text-gray-500 whitespace-nowrap">
                      {patient.name}
                    </td>
                    <td className="px-3 py-2.5 text-gray-800 font-semibold text-right whitespace-nowrap">
                      PKR {Number(patient.fee).toLocaleString()}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="bg-gray-50 border-t-2 border-gray-200">
                  <td
                    colSpan="5"
                    className="px-3 py-2.5 text-[10px] font-bold uppercase tracking-widest text-gray-500"
                  >
                    Total
                  </td>
                  <td className="px-3 py-2.5 text-gray-800 font-bold text-right whitespace-nowrap">
                    PKR {Number(totalFee).toLocaleString()}
                  </td>
                </tr>
              </tfoot>
            </table>
          ) : (
            <div className="text-center py-10 text-gray-400 text-sm">
              No patient records found.
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PatientsModal;
